export type TocLevel = 2 | 3;

export interface TocItem {
  level: TocLevel;
  text: string;
  id: string;
  line: number;
}

export class MarkdownSlugger {
  private seen = new Map<string, number>();

  slug(value: string): string {
    const base = slugifyBase(value);
    const count = this.seen.get(base) ?? 0;
    this.seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  }
}

export function extractMarkdownToc(markdown: string, levels: TocLevel[] = [2, 3]): TocItem[] {
  const levelSet = new Set<number>(levels);
  const slugger = new MarkdownSlugger();

  const items: TocItem[] = [];
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');

  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
    if (!match) continue;

    const level = match[1].length;
    if (!levelSet.has(level)) continue;

    const rawText = match[2].replace(/\s+#*\s*$/, '');
    const text = normalizeHeadingText(rawText);
    const id = slugger.slug(text);

    items.push({ level: level as TocLevel, text, id, line: i + 1 });
  }

  return items;
}

export function normalizeHeadingText(text: string): string {
  return text
    .trim()
    .replace(/[`*_~]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, '')
    .trim();
}

function slugifyBase(text: string): string {
  const normalized = normalizeHeadingText(text)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'section';
}
